import { Link } from "react-router-dom";
import { ImageIcon } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { useLang } from "@/i18n";
import MandalaDivider from "@/components/MandalaDivider";
import PageHeader from "@/components/PageHeader";

function ImagePlaceholder({ src, label, placeholderText, ratio = "4/3", className = "" }) {
  return (
    <figure
      className={`reveal relative overflow-hidden rounded-2xl ring-1 ring-[#E5DAC7] shadow-[0_18px_44px_rgba(60,40,20,0.10)] ${className}`}
      style={{ aspectRatio: ratio }}
    >
      {src ? (
        <img src={src} alt={label} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#F4EBDB] to-[#E6D5B8] flex flex-col items-center justify-center text-[#8a6a3a]">
          <ImageIcon size={34} className="opacity-60" />
          <div className="mt-3 eyebrow" style={{ color: "#8a6a3a" }}>{placeholderText}</div>
          <div className="mt-1 text-[13px] font-display text-[#7B1818]">{label}</div>
        </div>
      )}
      {src && (
        <figcaption className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.18em] text-[#7B1818] font-semibold">
          {label}
        </figcaption>
      )}
    </figure>
  );
}

export default function About() {
  const { t } = useLang();
  const a = t.about;
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow={a.eyebrow} title={a.title} sub={a.sub} />

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-3 reveal">
              <div className="eyebrow">{a.s1Eyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">
                {a.s1Title}
              </h2>
              <div className="mt-6"><MandalaDivider /></div>

              <div className="prose-devotional mt-10 space-y-6 text-[16px] text-[#3a2f24] leading-[1.9] font-serif-display">
                <p>{a.s1P1}</p>
                <p>{a.s1P2}</p>
                <p>{a.s1P3}</p>
              </div>

              <div className="mt-12">
                <div className="eyebrow">{a.s2Eyebrow}</div>
                <h3 className="mt-3 font-display text-2xl sm:text-3xl text-[#2A2118] leading-[1.15]">
                  {a.s2Title}
                </h3>
                <div className="prose-devotional mt-6 space-y-6 text-[16px] text-[#3a2f24] leading-[1.9] font-serif-display">
                  <p>{a.s2P1}</p>
                  <p>{a.s2P2}</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 lg:sticky lg:top-40">
              <ImagePlaceholder src={ASSETS.bannerMathaGate} label={a.img1} placeholderText={a.placeholder} ratio="4/5" />
              <p className="mt-3 text-[12px] text-[#8a6a3a] italic text-center">{a.img1Cap}</p>
            </div>
          </div>

          <div className="mt-28 grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <ImagePlaceholder src={ASSETS.bannerBronzeIdols} label={a.img2} placeholderText={a.placeholder} ratio="1/1" />
            </div>

            <div className="lg:col-span-3 reveal order-1 lg:order-2">
              <div className="eyebrow">{a.s3Eyebrow}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">
                {a.s3Title}
              </h2>
              <div className="mt-6"><MandalaDivider /></div>

              <div className="prose-devotional mt-10 space-y-6 text-[16px] text-[#3a2f24] leading-[1.9] font-serif-display">
                <p>{a.s3P1}</p>
                <p>{a.s3P2}</p>
              </div>
            </div>
          </div>

          <div className="mt-28 max-w-4xl mx-auto reveal text-center">
            <div className="eyebrow">{a.s4Eyebrow}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#2A2118] leading-[1.1]">
              {a.s4Title}
            </h2>
            <div className="mt-6 flex justify-center"><MandalaDivider /></div>

            <div className="prose-devotional mt-10 space-y-6 text-left text-[16px] text-[#3a2f24] leading-[1.9] font-serif-display">
              <p>{a.s4P1}</p>
              <p>{a.s4P2}</p>
            </div>

            <div className="mt-14 grid sm:grid-cols-2 gap-6">
              <ImagePlaceholder src={ASSETS.bannerDeitiesGarlanded} label={a.img3} placeholderText={a.placeholder} ratio="4/3" />
              <ImagePlaceholder label={a.img4} placeholderText={a.placeholder} ratio="4/3" />
            </div>
          </div>

          <div className="reveal mt-20 text-center">
            <Link to="/parampara" data-testid="about-view-parampara-btn" className="btn-primary">
              {a.cta}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
